import React, { useMemo, useState } from 'react';
import { ArrowLeft, Filter, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../../components/ui/dialog';
import { Button } from '../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { cn } from '../../../../../../../lib/utils';
import type { MaterialData } from '../../../../../types/materials';

type PipeDashboardModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  materials: MaterialData[];
  dashboardItems?: Partial<PipeDashboardItem>[];
  frequencyCoverage?: Partial<FrequencyCoverageRow>[];
  isLoading?: boolean;
  selectedPipe?: any;
  onOpenPipeReport?: (pipe: PipeDashboardItem) => void;
};

type ClearanceStatus = 'PASS' | 'PENDING_REVIEW' | 'FAIL' | 'INCOMPLETE';
type DashboardTab = 'kanban' | 'table' | 'funnel';

type PipeDashboardItem = {
  id: string | number;
  pipeNo: string;
  heatNumber: string;
  lotId: string;
  sequence: number;
  passing: number;
  totalChecks: number;
  clearance: ClearanceStatus;
};

type FrequencyCoverageRow = {
  reportType: string;
  frequency: string;
  covered: number;
  required: number;
  percent: number;
  pendingReview: boolean;
};

const STATUS_LABELS: Record<ClearanceStatus, string> = {
  PASS: 'Pass',
  PENDING_REVIEW: 'Pending review',
  FAIL: 'Fail',
  INCOMPLETE: 'Incomplete',
};

const STATUS_ORDER: ClearanceStatus[] = ['PASS', 'PENDING_REVIEW', 'FAIL', 'INCOMPLETE'];

const statusPillClass: Record<ClearanceStatus, string> = {
  PASS: 'border-[#A7DCC2] bg-[#E8F8EF] text-[#20865F]',
  PENDING_REVIEW: 'border-[#F0D99A] bg-[#FFF3D8] text-[#8A6500]',
  FAIL: 'border-[#F1B7B8] bg-[#FDE9E9] text-readable-red',
  INCOMPLETE: 'border-[#D8DDE5] bg-[#F2F4F7] text-heritage-grey',
};

const normalizeClearance = (value: unknown): ClearanceStatus => {
  const status = String(value || '').toUpperCase();
  if (!status) return 'INCOMPLETE';
  if (status === 'FAIL') return 'FAIL';
  if (status === 'PENDING_REVIEW' || status === 'PENDING') return 'PENDING_REVIEW';
  if (status === 'INCOMPLETE') return 'INCOMPLETE';
  return 'PASS';
};

const getPipeData = (material: MaterialData): any =>
  (material.details as any)?.pipeExtraDetails?.pipeData ||
  (material.details as any)?.pipe_extra_details?.pipeData ||
  (material.details as any)?.pipe_extra_details?.pipe_data ||
  {};

const toDashboardItem = (material: MaterialData): PipeDashboardItem | null => {
  const details = material.details as any;
  const pipeData = getPipeData(material);
  const heatNumber = String(details?.pipeHeatNumber || details?.pipe_heat_number || '').trim();
  if (!heatNumber) return null;

  const sequence = Number(pipeData?.sequence || details?.pipeNo || material.materialId || 0);
  return {
    id: material.materialId ?? details?.pipePipeId ?? details?.pipeNo ?? `${heatNumber}-${sequence}`,
    pipeNo: String(details?.pipeNo || details?.pipe_no || details?.pipePipeId || material.materialId || sequence),
    heatNumber,
    lotId: String(pipeData?.lotId || pipeData?.lot_id || 'LOT-UNASSIGNED'),
    sequence,
    passing: Number(pipeData?.passing || 0),
    totalChecks: 18,
    clearance: normalizeClearance(pipeData?.clearance),
  };
};

const percent = (count: number, total: number) => total ? Math.round((count / total) * 100) : 0;

const normalizeDashboardItem = (item: Partial<PipeDashboardItem>, index: number): PipeDashboardItem => ({
  id: item.id ?? item.pipeNo ?? `dashboard-pipe-${index}`,
  pipeNo: String(item.pipeNo || item.id || index + 1),
  heatNumber: String(item.heatNumber || '-'),
  lotId: String(item.lotId || 'LOT-UNASSIGNED'),
  sequence: Number(item.sequence || index + 1),
  passing: Number(item.passing || 0),
  totalChecks: Number(item.totalChecks || 18),
  clearance: normalizeClearance(item.clearance),
});

const normalizeFrequencyCoverageRow = (item: Partial<FrequencyCoverageRow> & Record<string, unknown>): FrequencyCoverageRow => {
  const covered = Number(item.covered ?? item.cov ?? 0);
  const required = Number(item.required ?? item.need ?? 0);
  const rawPercent = item.percent ?? item.pct;
  return {
    reportType: String(item.reportType ?? item.report_type ?? item.t ?? '').trim().toUpperCase() || 'REPORT',
    frequency: String(item.frequency ?? item.freq ?? 'per_pipe'),
    covered,
    required,
    percent: Number.isFinite(Number(rawPercent)) ? Number(rawPercent) : percent(covered, required),
    pendingReview: Boolean(item.pendingReview ?? item.pending_review ?? item.blocked),
  };
};

export const PipeDashboardModal = ({
  isOpen,
  onOpenChange,
  materials,
  dashboardItems,
  frequencyCoverage = [],
  isLoading = false,
  selectedPipe,
  onOpenPipeReport,
}: PipeDashboardModalProps) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('kanban');
  const pipeItems = useMemo(
    () => (
      dashboardItems
        ? dashboardItems.map(normalizeDashboardItem)
        : materials.map(toDashboardItem).filter(Boolean) as PipeDashboardItem[]
    ).sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
    [dashboardItems, materials],
  );

  const grouped = useMemo(() => {
    const groups: Record<ClearanceStatus, PipeDashboardItem[]> = {
      PASS: [],
      PENDING_REVIEW: [],
      FAIL: [],
      INCOMPLETE: [],
    };
    pipeItems.forEach((pipe) => groups[pipe.clearance].push(pipe));
    return groups;
  }, [pipeItems]);

  const summaryCards = useMemo(() => {
    const total = pipeItems.length;
    const cleared = grouped.PASS.length;
    const failing = grouped.FAIL.length;
    const pending = grouped.PENDING_REVIEW.length;
    const incomplete = grouped.INCOMPLETE.length;
    return [
      { label: 'Total Pipes', value: total, sub: 'in scope' },
      { label: 'Cleared', value: cleared, sub: `${percent(cleared, total)}%`, status: 'PASS' as ClearanceStatus },
      { label: 'Failing', value: failing, sub: `${percent(failing, total)}%`, status: 'FAIL' as ClearanceStatus },
      { label: 'Pending Review', value: pending, sub: `${percent(pending, total)}%`, status: 'PENDING_REVIEW' as ClearanceStatus },
      { label: 'Incomplete', value: incomplete, sub: `${percent(incomplete, total)}%`, status: 'INCOMPLETE' as ClearanceStatus },
    ];
  }, [grouped, pipeItems.length]);
  const frequencyCoverageRows = useMemo(
    () => frequencyCoverage.map(normalizeFrequencyCoverageRow),
    [frequencyCoverage]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="!left-0 !top-0 !h-screen !max-h-screen !w-screen !max-w-none !translate-x-0 !translate-y-0 m-0 gap-0 rounded-none border-0 bg-page-bg p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 pb-3 pt-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <Button onClick={() => onOpenChange(false)} variant="outline" className="h-9 w-9 shrink-0 rounded-lg border-[#E2E5EA] p-0">
                  <ArrowLeft className="h-5 w-5 text-text-grey" />
                </Button>
                <div>
                  <DialogTitle className="font-heading text-[20px] font-semibold text-blueprint-blue">
                    Pipe Clearance Dashboard
                  </DialogTitle>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-heritage-grey">
                    <span>{selectedPipe?.displayName || selectedPipe?.name || 'Selected pipe'}</span>
                    <span>-</span>
                    <span>{pipeItems.length} pipes</span>
                    <span>-</span>
                    <span>{grouped.FAIL.length + grouped.PENDING_REVIEW.length + grouped.INCOMPLETE.length} exceptions</span>
                  </div>
                </div>
              </div>
              <Button type="button" variant="outline" className="border-[#E2E5EA] bg-white text-blueprint-blue hover:bg-page-bg">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              {summaryCards.map((card) => (
                <Card key={card.label} className="border-[#E2E5EA] bg-white">
                  <CardContent className="p-5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{card.label}</div>
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[26px] font-semibold text-blueprint-blue">{card.value}</span>
                        <span className="text-[11px] text-heritage-grey">{card.sub}</span>
                      </div>
                      {card.status && <StatusPill status={card.status} />}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-4 overflow-hidden border-[#E2E5EA] bg-white">
              <CardContent className="p-0">
                <div className="border-b border-[#E2E5EA] px-5 py-4">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Frequency coverage by report type</h3>
                </div>
                {frequencyCoverageRows.length > 0 ? (
                  <div className="divide-y divide-[#EEF0F3]">
                    {frequencyCoverageRows.map((row) => {
                      const pct = Math.min(Math.max(row.percent, 0), 100);
                      const isComplete = row.required > 0 && row.covered >= row.required && !row.pendingReview;
                      const barClassName = row.pendingReview ? 'bg-amber-500' : isComplete ? 'bg-[#20865F]' : 'bg-hero-red';

                      return (
                        <div key={`${row.reportType}-${row.frequency}`} className="grid grid-cols-[128px_70px_90px_minmax(160px,1fr)_56px_140px] items-center gap-3 px-5 py-3">
                          <span className="font-mono text-[12px] font-semibold text-blueprint-blue">{row.reportType}</span>
                          <span className="font-mono text-[11px] text-heritage-grey">{row.frequency.replace(/^per_/, '')}</span>
                          <span className="font-mono text-[12px] text-text-grey">{row.covered} / {row.required}</span>
                          <span className="h-1.5 rounded-full bg-[#EEF0F3]">
                            <span className={cn('block h-1.5 rounded-full', barClassName)} style={{ width: `${pct}%` }} />
                          </span>
                          <span className="text-right font-mono text-[11px] text-heritage-grey">{pct}%</span>
                          <span className="justify-self-end">
                            {row.pendingReview ? <StatusPill status="PENDING_REVIEW" /> : isComplete ? <StatusPill status="PASS" /> : <StatusPill status="INCOMPLETE" />}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-5 py-6 text-[12px] text-heritage-grey">
                    No active frequency rules found for this project. Add project limits and frequency rules to show coverage.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-4 overflow-hidden border-[#E2E5EA] bg-white">
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E5EA] px-5 py-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Clearance roll-up</h3>
                    <div className="flex rounded-lg bg-page-bg p-1">
                      {(['kanban', 'table', 'funnel'] as DashboardTab[]).map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setActiveTab(tab)}
                          className={cn(
                            'rounded-md px-3 py-1.5 text-[11px] font-semibold capitalize transition-colors',
                            activeTab === tab ? 'bg-white text-blueprint-blue shadow-sm' : 'text-heritage-grey hover:text-blueprint-blue',
                          )}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                    <span className="text-[11px] text-heritage-grey">change via tweaks --</span>
                  </div>
                  <Button type="button" variant="outline" size="sm" className="border-[#E2E5EA] bg-white text-blueprint-blue">
                    View all pipes
                  </Button>
                </div>

                {isLoading ? (
                  <div className="flex min-h-[420px] flex-col items-center justify-center p-8 text-center">
                    <Loader2 className="h-9 w-9 animate-spin text-hero-red" />
                    <div className="mt-4 font-heading text-[16px] font-semibold text-blueprint-blue">Loading dashboard data</div>
                    <div className="mt-2 text-[13px] text-heritage-grey">Fetching pipe clearance status from the API.</div>
                  </div>
                ) : !pipeItems.length ? (
                  <div className="flex min-h-[320px] flex-col items-center justify-center p-8 text-center">
                    <div className="font-heading text-[16px] font-semibold text-blueprint-blue">No dashboard data found</div>
                    <div className="mt-2 text-[13px] text-heritage-grey">No pipe dashboard rows were returned for this project selection.</div>
                  </div>
                ) : activeTab === 'kanban' && (
                  <div className="grid grid-cols-1 divide-y divide-[#E2E5EA] lg:grid-cols-4 lg:divide-x lg:divide-y-0">
                    {STATUS_ORDER.map((status) => (
                      <KanbanColumn key={status} status={status} pipes={grouped[status]} onOpenPipeReport={onOpenPipeReport} />
                    ))}
                  </div>
                )}

                {!isLoading && pipeItems.length > 0 && activeTab === 'table' && (
                  <div className="overflow-auto">
                    <table className="w-full text-left">
                      <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                        <tr>
                          <th className="px-4 py-3">Pipe</th>
                          <th className="px-4 py-3">Heat</th>
                          <th className="px-4 py-3">Lot</th>
                          <th className="px-4 py-3 text-right">Checks</th>
                          <th className="px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#EEF0F3] text-[12px]">
                        {pipeItems.slice(0, 80).map((pipe) => (
                          <tr key={pipe.id} onClick={() => onOpenPipeReport?.(pipe)} className={cn(onOpenPipeReport && 'cursor-pointer hover:bg-page-bg')}>
                            <td className="px-4 py-3 font-mono font-semibold text-blueprint-blue">{pipe.pipeNo}</td>
                            <td className="px-4 py-3 font-mono text-text-grey">{pipe.heatNumber}</td>
                            <td className="px-4 py-3 font-mono text-text-grey">{pipe.lotId}</td>
                            <td className="px-4 py-3 text-right font-mono text-text-grey">{pipe.passing}/{pipe.totalChecks}</td>
                            <td className="px-4 py-3"><StatusPill status={pipe.clearance} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {!isLoading && pipeItems.length > 0 && activeTab === 'funnel' && (
                  <div className="grid gap-4 p-6 md:grid-cols-4">
                    {STATUS_ORDER.map((status) => (
                      <div key={status} className="rounded-xl border border-[#E2E5EA] bg-page-bg p-4">
                        <StatusPill status={status} />
                        <div className="mt-4 font-mono text-[30px] font-semibold text-blueprint-blue">{grouped[status].length}</div>
                        <div className="mt-1 text-[12px] text-heritage-grey">{percent(grouped[status].length, pipeItems.length)}% of pipes</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StatusPill = ({ status }: { status: ClearanceStatus }) => (
  <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', statusPillClass[status])}>
    <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current" />
    {STATUS_LABELS[status]}
  </span>
);

const KanbanColumn = ({ status, pipes, onOpenPipeReport }: { status: ClearanceStatus; pipes: PipeDashboardItem[]; onOpenPipeReport?: (pipe: PipeDashboardItem) => void }) => {
  const visiblePipes = pipes.slice(0, 8);
  return (
    <div className="min-h-[520px] p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <StatusPill status={status} />
        <span className="font-mono text-[11px] text-heritage-grey">{pipes.length}</span>
      </div>
      <div className="space-y-2">
        {visiblePipes.map((pipe) => (
          <div
            key={pipe.id}
            onClick={() => onOpenPipeReport?.(pipe)}
            className={cn('rounded-lg border border-[#E2E5EA] bg-white p-3 shadow-sm', onOpenPipeReport && 'cursor-pointer hover:border-hero-red-20 hover:bg-page-bg')}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="font-mono text-[13px] font-semibold text-blueprint-blue">{pipe.pipeNo}</div>
              <div className="font-mono text-[10px] text-heritage-grey">{pipe.passing}/{pipe.totalChecks}</div>
            </div>
            <div className="mt-2 font-mono text-[10px] text-heritage-grey">
              {pipe.heatNumber} - {pipe.lotId}
            </div>
          </div>
        ))}
        {pipes.length > visiblePipes.length && (
          <div className="pt-1 text-[11px] text-heritage-grey">+ {pipes.length - visiblePipes.length} more</div>
        )}
      </div>
    </div>
  );
};
